import { useEffect, useState } from "react";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { Option } from "react-bootstrap-typeahead/types/types";
import { getSearchedLocations } from "src/redux/services/practiceServices";
import { Address, AddressObj, LocationSearchResponse, PageQuestionChoice } from "../../types";
import "./TypeAheadInput.scss";

type Props = {
    choices: any[];
    onLocationSelection: (selectedOption: PageQuestionChoice, choiceId: string) => void;
    type: 'questionnaire' | 'CAfilters',
    disabled?: boolean;
};

export function TypeAheadLocationSearch({
    choices,
    onLocationSelection,
    type,
    disabled
}: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState<Address[]>([]);
    const [selected, setSelected] = useState<Option[]>();
    const [selectedValue, setSelectedValue] = useState<Option[]>();
    const [isError, ] = useState(false);
    const choice = choices[0];

    useEffect(() => {
        if (choices[0]) {
            const _choice = choices[0];
            const _choiceValue: Address = _choice?.value
                ? JSON.parse(_choice?.value)
                : undefined;

            if (_choiceValue) {
                setSelected([_choiceValue]);
                setSelectedValue([_choiceValue]);
            }
        }
    }, []);

    const handleSearch = (query: string) => {
        setIsLoading(true);
        setOptions([]);
        fetchSearchedLocations(query);
    };

    const fetchSearchedLocations = async (keyword: string) => {
        const locations: LocationSearchResponse = await getSearchedLocations(
            keyword
        );

        if (locations?.locationDtoList?.length) {
            setOptions(formatAddressForDisplay(locations.locationDtoList));
        } else {
            setOptions([]);
        }

        setIsLoading(false);
    };

    function formatAddressForDisplay(data: Address[]): AddressObj[] {
        const reformattedArray: AddressObj[] = data.map((address: Address) =>

            // Changed ["displayLabel"] to displayLabel below to fix the eslint warning.
            Object.assign(address, {
                displayLabel: `${address.city}, ${address.stateCode} ${address.zipCode}, ${address.countryCode}`,
            })
        );

        return reformattedArray;
    }

    // Bypass client-side filtering by returning `true`. Results are already
    // filtered by the search endpoint, so no need to do it again.
    const filterBy = () => true;

    const onSelectionChange = (newSelection: Option[]) => {
        setSelected(newSelection);
    
        if (newSelection?.length) {
            setSelectedValue(newSelection);
            const selectedOption = newSelection[0] as PageQuestionChoice; // Cast to PageQuestionChoice
            
            onLocationSelection(selectedOption, choice.id);
        }
    };

    return (
        <div className="typeahead-location-search-container">
            <div className={type === 'questionnaire' ? "" : "w-100"}>
                <AsyncTypeahead
                    id={choice?.unique_key || "typeahead-location-search"}
                    className={`typeahead-location-search ${type === 'questionnaire' ? "questionnaire-location-search" : "filter-location-search"}`}
                    filterBy={filterBy}
                    onChange={onSelectionChange}
                    isLoading={isLoading}
                    labelKey="displayLabel"
                    selected={selected}
                    minLength={5}
                    onSearch={handleSearch}
                    options={options}
                    placeholder={choice?.place_holder}
                    onBlur={() => {
                        setSelected(selectedValue);
                    }}
                    renderMenuItemChildren={(option: any) => (
                        <span>{`${option.city}, ${option.stateCode} ${option.zipCode}, ${option.countryCode}`}</span>
                    )}
                    disabled={disabled}
                />
                {isError && (
                    <label className="invalid-feedback">
                        Some error occurred.
                    </label>
                )}
            </div>
        </div>
    );
}
